import type { ReactNode } from "react";
import { useState } from "react";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import type { UUID } from "@/Shared/types";
import type { SavedCommodity } from "@freightsimple/generated-dashboard-openapi-client/models/SavedCommodity";
import { useOnce } from "@/Hooks/useOnce";
import { AddModifySavedCommodityDialogBody } from "../Dialogs/Internal/AddModifySavedCommodityDialogBody";
import { useSavedCommoditiesService } from "../Services/SavedCommoditiesService";

interface AddModifyDialogState {
  /** Set when modifying; add mode otherwise. */
  savedCommodityToModify: SavedCommodity | undefined;
  /** Remount key so the form starts fresh every time the dialog opens. */
  openCount: number;
}

/**
 * Owns the Saved Commodities screen's data and actions — load, add, modify,
 * delete (with a confirm), and the optional Shopify product sync — so the
 * screen itself is render-only. Also owns the screen's dialogs (declarative):
 * render the returned `dialogs` node.
 */
export function useSavedCommoditiesScreen() {
  const savedCommoditiesService = useSavedCommoditiesService();
  const [loading, setLoading] = useState(false);
  const [savedCommodities, setSavedCommodities] = useState<
    undefined | Array<SavedCommodity>
  >();
  const [syncingFromShopify, setSyncingFromShopify] = useState(false);
  const [shopifyConnected, setShopifyConnected] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<UUID | undefined>();
  const [addModifyDialog, setAddModifyDialog] = useState<
    AddModifyDialogState | undefined
  >();
  const [notice, setNotice] = useState<
    { title: string; message: string } | undefined
  >();

  async function loadSavedCommodities() {
    setLoading(true);
    const response = await savedCommoditiesService.getAllSavedCommodities();
    setSavedCommodities(
      response.sort((a, b) => a.description.localeCompare(b.description)),
    );
    setLoading(false);
  }

  useOnce(loadSavedCommodities);

  // Only offer "Sync from Shopify" when this company actually has a connected
  // Shopify store — Shopify eligibility is ops-controlled (a company_integration
  // record), so most customers never see the button.
  useOnce(async () => {
    try {
      const status = await savedCommoditiesService.getShopifySyncStatus();
      setShopifyConnected(status.connected);
    } catch {
      setShopifyConnected(false);
    }
  });

  function onDelete(savedCommodityId: UUID) {
    setDeleteTarget(savedCommodityId);
  }

  async function confirmDelete() {
    if (deleteTarget === undefined) {
      return;
    }
    await savedCommoditiesService.deleteSavedCommodity(deleteTarget);
    setDeleteTarget(undefined);
    await loadSavedCommodities();
  }

  function onAdd() {
    setAddModifyDialog((previous) => ({
      savedCommodityToModify: undefined,
      openCount: (previous?.openCount ?? 0) + 1,
    }));
  }

  function onModify(savedCommodity: SavedCommodity) {
    setAddModifyDialog((previous) => ({
      savedCommodityToModify: savedCommodity,
      openCount: (previous?.openCount ?? 0) + 1,
    }));
  }

  async function onSyncFromShopify() {
    setSyncingFromShopify(true);
    try {
      const result = await savedCommoditiesService.syncFromShopify();
      if (!result.linked) {
        setNotice({
          title: "No Shopify store connected",
          message:
            "Connect a Shopify store to FreightSimple before syncing products.",
        });
        return;
      }
      await loadSavedCommodities();

      const messageParts = [
        `Synced ${result.totalProducts} Shopify product(s) — created ${result.stubsCreated} new saved commodity(ies).`,
      ];
      if (result.needsDimensions > 0) {
        messageParts.push(
          `Heads up: Shopify doesn't share product dimensions with us, so ${result.needsDimensions} still need dimensions before they can be quoted. ` +
            `Please open the new saved commodities and fill in the dimensions (and double-check the rest of the details are what you need).`,
        );
      }
      setNotice({
        title: "Synced from Shopify",
        message: messageParts.join(" "),
      });
    } catch {
      setNotice({
        title: "Sync failed",
        message:
          "Couldn't sync products from Shopify. Please try again, or contact FreightSimple.",
      });
    } finally {
      setSyncingFromShopify(false);
    }
  }

  const dialogs: ReactNode = (
    <>
      <Dialog
        open={deleteTarget !== undefined}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(undefined);
        }}
        title="Confirm delete"
        description="Are you sure you want to delete this commodity?"
        okText="Delete Commodity"
        onOk={confirmDelete}
      >
        <ControlPresentationProvider presentation="underlined">
          <></>
        </ControlPresentationProvider>
      </Dialog>
      <Dialog
        open={addModifyDialog !== undefined}
        onOpenChange={(open) => {
          if (!open) setAddModifyDialog(undefined);
        }}
        title="Saved Commodity"
        description="Enter details of something you ship often to speed up requesting quotes"
        width="wide"
      >
        <ControlPresentationProvider presentation="underlined">
          {addModifyDialog && (
            <AddModifySavedCommodityDialogBody
              key={addModifyDialog.openCount}
              savedCommodityToModify={addModifyDialog.savedCommodityToModify}
              onConfirm={async (savedCommodity) => {
                if (addModifyDialog.savedCommodityToModify !== undefined) {
                  await savedCommoditiesService.modifySavedCommodity(
                    savedCommodity,
                  );
                } else {
                  await savedCommoditiesService.addSavedCommodity(
                    savedCommodity,
                  );
                }
                setAddModifyDialog(undefined);
                await loadSavedCommodities();
              }}
              onCancel={() => setAddModifyDialog(undefined)}
            />
          )}
        </ControlPresentationProvider>
      </Dialog>
      <Dialog
        open={notice !== undefined}
        onOpenChange={(open) => {
          if (!open) setNotice(undefined);
        }}
        title={notice?.title}
        footer={
          <Button primary onClick={() => setNotice(undefined)}>
            OK
          </Button>
        }
      >
        <ControlPresentationProvider presentation="underlined">
          {notice?.message}
        </ControlPresentationProvider>
      </Dialog>
    </>
  );

  return {
    savedCommodities,
    loading,
    shopifyConnected,
    syncingFromShopify,
    onAdd,
    onModify,
    onDelete,
    onSyncFromShopify,
    dialogs,
  };
}
